import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonSearchbar,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { checkmark, chevronDown } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";

export default function SearchSelect({
  label,
  placeholder = "Select option",
  options,
  value,
  multiple = false,
  onChange,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [draftValue, setDraftValue] = useState(multiple ? value || [] : value || "");

  useEffect(() => {
    setDraftValue(multiple ? value || [] : value || "");
  }, [multiple, value]);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(term));
  }, [options, search]);

  const selectedLabels = multiple
    ? options.filter((option) => (value || []).includes(option.value)).map((option) => option.label)
    : options.filter((option) => option.value === value).map((option) => option.label);

  function open() {
    setSearch("");
    setDraftValue(multiple ? value || [] : value || "");
    setIsOpen(true);
  }

  function close() {
    setIsOpen(false);
    setSearch("");
  }

  function select(optionValue) {
    if (!multiple) {
      onChange(optionValue);
      close();

      return;
    }

    const current = draftValue || [];

    if (current.includes(optionValue)) {
      setDraftValue(current.filter((selectedValue) => selectedValue !== optionValue));

      return;
    }

    setDraftValue([...current, optionValue]);
  }

  function applySelection() {
    onChange(draftValue);
    close();
  }

  return (
    <>
      <button className="search-select-trigger" type="button" onClick={open}>
        {label && <span className="search-select-label">{label}</span>}
        <span className={selectedLabels.length ? "search-select-value" : "search-select-placeholder"}>
          {selectedLabels.length ? selectedLabels.join(", ") : placeholder}
        </span>
        <IonIcon icon={chevronDown} />
      </button>

      <IonModal isOpen={isOpen} onDidDismiss={close} className="selection-modal">
        <IonHeader>
          <IonToolbar>
            <IonTitle>{label || "Select"}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={multiple ? applySelection : close}>{multiple ? "Done" : "Close"}</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <main className="selection-content">
            <IonSearchbar
              value={search}
              onIonInput={(event) => setSearch(event.detail.value ?? "")}
              placeholder={`Search ${label?.toLowerCase() || "options"}`}
              debounce={0}
            />
            <section className="selection-list">
              {filteredOptions.map((option) => {
                const selected = multiple ? (draftValue || []).includes(option.value) : draftValue === option.value;

                return (
                  <button className="selection-option" type="button" key={option.value} onClick={() => select(option.value)}>
                    <span>{option.label}</span>
                    {multiple ? <IonCheckbox checked={selected} /> : selected && <IonIcon icon={checkmark} />}
                  </button>
                );
              })}
              {filteredOptions.length === 0 && <div className="empty-state">No options found.</div>}
            </section>
          </main>
        </IonContent>
      </IonModal>
    </>
  );
}
